import React, { useMemo, useState, useCallback, useRef } from 'react';
import { ProductApi } from 'common/types';
import { useSelector } from 'react-redux';
import { Box, Typography, Popper, List, Paper, Divider } from '@mui/material';
import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { createFilterOptions } from '@mui/material/Autocomplete';

import useFocusWithin from 'common/hooks/useFocusWithin';
import SearchInput from 'common/components/SearchInput';

import * as StockSelectors from 'selectors/StockSelectors';

import ProductListItem from './ProductListItem';

import { useTranslation } from 'services/localization/useTranslation';
import { isLiftTicketProduct } from 'common/utils/liftTicketUtils';

interface Props {
	onSelect: (product: ProductApi) => void;
	hiddenIds?: string[];
	hideSetProducts?: boolean;
	placeholder?: string;
	disabled?: boolean;
}

interface Option {
	id: string;
	label: string;
	firstLetter: string;
	product: ProductApi;
}

const getFirstLetter = (label: string) => {
	switch (true) {
		case !isNaN(Number[label]):
			return '0-9';
		default:
			return label[0]?.toUpperCase() ?? '';
	}
};

const filterOptions = createFilterOptions<Option>({
	stringify: (option) => option.label,
});

const ProductSearch = (props: Props) => {
	const { onSelect, hiddenIds = [], hideSetProducts = false } = props;
	const classes = useStyles();
	const { getTranslation } = useTranslation();
	const anchorEl = useRef(null);
	const [hasFocus, setHasFocus, focusProps] = useFocusWithin();
	const [inputValue, setInputValue] = useState('');
	const [inputKey, setInputKey] = useState(0);
	const products = useSelector(StockSelectors.stockProductsData);

	const { t } = useTranslation();

	const handleSelect = useCallback(
		(product: ProductApi) => {
			onSelect(product);
			setHasFocus(false);
			setInputKey(inputKey + 1);
		},
		[onSelect, setHasFocus, inputKey],
	);

	const options = useMemo(() => {
		return products
			.filter((product) => {
				if (!product.name.def) return false;
				if (hiddenIds.indexOf(product.id) !== -1) return false;
				if (hideSetProducts && product.set) return false;
				if (isLiftTicketProduct(product)) return false;

				return true;
			})
			.map((product) => {
				const label = getTranslation(product.name);
				return {
					id: product.id,
					label,
					firstLetter: getFirstLetter(label),
					product,
				};
			})
			.sort((a, b) => a.firstLetter.localeCompare(b.firstLetter));
	}, [products, hiddenIds, hideSetProducts, getTranslation]);

	const filteredOptions = filterOptions(options, {
		inputValue,
		getOptionLabel: (option) => option.label,
	});

	return (
		<div className={classes.wrapper} ref={anchorEl} {...focusProps}>
			<SearchInput
				textFieldProps={{
					placeholder: props.placeholder,
					disabled: props.disabled,
				}}
				onChange={setInputValue}
				key={inputKey}
				t={t}
			/>

			<Popper
				style={{ minWidth: '100%', zIndex: 1000 }}
				disablePortal
				open={hasFocus}
				anchorEl={anchorEl.current}
				placement="bottom-end"
			>
				<Box mt={1} mb={1} width="100%">
					<Paper elevation={3} className={classes.listPaper}>
						{filteredOptions.length > 0 ? (
							<List>
								{filteredOptions.map((option, index) => (
									<React.Fragment key={option.id}>
										{index !== 0 && <Divider component="li" variant="inset" />}
										<ProductListItem product={option.product} onClick={handleSelect} />
									</React.Fragment>
								))}
							</List>
						) : (
							<Box p={2}>
								<Typography variant="body1" align="center">
									{t('common:states.noResults', 'No results')}
								</Typography>
							</Box>
						)}
					</Paper>
				</Box>
			</Popper>
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			position: 'relative',
		},
		listPaper: {
			maxHeight: '300px',
			overflowY: 'auto',
		},
	}),
);

export default ProductSearch;
